import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, MapPin, MessageCircle, ChevronRight, ArrowRight } from "lucide-react";

// NEW LOGO AND ASSETS
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/30awoyhb_Ein%20bisschen%20Text%20hinzuf%C3%BCgen%20%2848%29.png",
  heroNew: "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/6tx2syce_generated-image%20%287%29.png",
  gallery: [
    "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/ecczykvk_473883047_1123212876149444_9174795922071890903_n.jpg",
    "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/xe6xhcph_475018692_1128395688964496_1718984876067342420_n.jpg",
    "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/giec5gz5_475407041_1128395698964495_2388378341396444371_n.jpg",
    "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/lce08pqf_475094789_1128395598964505_5973279620611796966_n.jpg",
    "https://customer-assets.emergentagent.com/job_f1db6790-e15c-451f-9967-841f382e5fec/artifacts/yh52pxvf_474698195_1127719895813667_2102248518292115633_n.jpg",
    "https://customer-assets.emergentagent.com/job_f1db6790-e15c-451f-9967-841f382e5fec/artifacts/bq7mnukz_475063574_1128435099075480_1980426865821911075_n.jpg",
    "https://customer-assets.emergentagent.com/job_f1db6790-e15c-451f-9967-841f382e5fec/artifacts/c773rz3o_474610814_1128435079075482_4139223867784183719_n.jpg",
    "https://customer-assets.emergentagent.com/job_f1db6790-e15c-451f-9967-841f382e5fec/artifacts/xb7e0nq1_474621130_1128916885693968_1327789669679621614_n.jpg",
    "https://customer-assets.emergentagent.com/job_agrar-premium-design/artifacts/ql9ta21l_471433290_1107458221058243_1745801782008747976_n.jpg"
  ]
};

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Navbar Component - ORANGE THEME
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ONLY 5 menu items - NO DUPLICATE KONTAKT
  const navLinks = [
    { to: "/", label: "Start" },
    { to: "/leistungen", label: "Leistungen" },
    { to: "/ueber-uns", label: "Über Uns" },
    { to: "/galerie", label: "Galerie" },
    { to: "/kontakt", label: "Kontakt" }
  ];

  const navBg = scrolled || !isHome 
    ? "bg-white shadow-md" 
    : "bg-black/20 backdrop-blur-sm";
  
  const textColor = scrolled || !isHome 
    ? "text-gray-800" 
    : "text-white";

  const logoFilter = scrolled || !isHome ? "" : "brightness-0 invert";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex-shrink-0">
            <img
              src={ASSETS.logo}
              alt="Linhardsberger Agrarservice"
              className={`h-14 sm:h-16 object-contain transition-all duration-300 ${logoFilter}`}
            />
          </Link>

          {/* Desktop Navigation - ONLY 5 ITEMS */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-link ${textColor} hover:text-[#a84025] py-2`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            className={`lg:hidden p-2 z-50 ${textColor}`}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100 shadow-lg absolute left-0 right-0 top-20"
            >
              <div className="py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className="block px-6 py-3 text-gray-800 font-semibold uppercase tracking-wide hover:bg-gray-50 hover:text-[#a84025] transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Hero Section - WITH HACKGUTERZEUGUNG
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroNew}
          alt="Mähdrescher im Einsatz"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-4xl mx-auto"
        >
          <motion.h1
            variants={fadeIn}
            className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4 text-shadow tracking-widest"
          >
            LINHARDSBERGER
          </motion.h1>
          
          <motion.p
            variants={fadeIn}
            className="text-[#a84025] text-lg md:text-xl font-semibold tracking-[0.2em] uppercase mb-8"
          >
            Agrarservice & Hackguterzeugung
          </motion.p>
          
          <motion.p
            variants={fadeIn}
            className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-4 font-light"
          >
            Alles aus einer Hand für Ihre Ernte und Holzverarbeitung.
          </motion.p>
          
          <motion.p
            variants={fadeIn}
            className="text-base md:text-lg text-white/70 mb-12 tracking-wide"
          >
            Zuverlässig. Leistungsstark. Modern.
          </motion.p>
          
          <motion.div 
            variants={fadeIn}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/kontakt"
              data-testid="hero-cta-primary"
              className="btn-primary text-base"
            >
              Kontakt aufnehmen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/leistungen"
              data-testid="hero-cta-secondary"
              className="btn-secondary text-base"
            >
              Unsere Leistungen
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-[#a84025] rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Highlight Bar - Key Services with Modern Icons
const HighlightBar = () => {
  const highlights = [
    { title: "Hackguterzeugung", desc: "Professionelle Holzverarbeitung" },
    { title: "Erntearbeiten", desc: "Modernste Technik" },
    { title: "Transporte", desc: "Zuverlässige Logistik" }
  ];

  return (
    <section className="bg-secondary py-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {highlights.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="flex items-center gap-4 justify-center md:justify-start"
            >
              <div className="w-12 h-12 rounded-lg bg-[#a84025]/20 flex items-center justify-center">
                {idx === 0 && (
                  <svg className="w-6 h-6 text-[#a84025]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                )}
                {idx === 1 && (
                  <svg className="w-6 h-6 text-[#a84025]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                  </svg>
                )}
                {idx === 2 && (
                  <svg className="w-6 h-6 text-[#a84025]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="font-heading text-white text-lg tracking-wider">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Hackguterzeugung Section
const HackguterzeugungSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="order-2 lg:order-1"
          >
            <p className="text-[#a84025] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Holzverarbeitung
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mb-6 tracking-wider">
              HACKGUTERZEUGUNG
            </h2>
            <div className="w-20 h-1 bg-[#a84025] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              Wir bieten professionelle Hackguterzeugung mit moderner Technik für verschiedenste Anforderungen.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Ob Energieholz, Landschaftspflege oder individuelle Lösungen – wir sorgen für effiziente und saubere Verarbeitung.
            </p>
            <ul className="space-y-3 mb-8">
              {["Energieholz-Produktion", "Landschaftspflege", "Individuelle Lösungen", "Effiziente Verarbeitung"].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-gray-700">
                  <span className="w-2 h-2 bg-[#a84025] rounded-full flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/leistungen"
              className="inline-flex items-center text-[#a84025] font-semibold hover:text-[#8a3520] transition-colors"
            >
              Alle Leistungen ansehen
              <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="order-1 lg:order-2"
          >
            <div className="relative">
              <img
                src={ASSETS.gallery[4]}
                alt="Hackguterzeugung"
                className="w-full rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#a84025] text-white p-6 rounded-lg shadow-lg hidden md:block">
                <p className="font-heading text-3xl tracking-wider">20+</p>
                <p className="text-sm text-white/80">Jahre Erfahrung</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Preview - RUST THEME
const ServicesPreview = () => {
  const services = [
    { title: "Hackguterzeugung", desc: "Professionelle Holzverarbeitung" },
    { title: "Maishäcksler", desc: "Professionelle Maisernte" },
    { title: "Mähdrescher", desc: "Mit Hangausgleich" },
    { title: "Ballenpressen", desc: "Rund- & Quaderballen" }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
          className="text-center mb-16"
        >
          <p className="text-[#a84025] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
            Was wir bieten
          </p>
          <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mb-6 tracking-wider">
            UNSERE LEISTUNGEN
          </h2>
          <div className="w-20 h-1 bg-[#a84025] mx-auto rounded-full" />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              variants={fadeIn}
              className="bg-gray-50 rounded-lg p-8 text-center hover:bg-[#a84025] hover:text-white transition-all duration-300 group cursor-pointer border border-gray-100 hover:border-[#a84025]"
            >
              <h3 className="font-heading text-xl mb-2 group-hover:text-white text-gray-900 tracking-wider">
                {service.title}
              </h3>
              <p className="text-gray-600 group-hover:text-white/80 text-sm">
                {service.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center mt-12"
        >
          <Link
            to="/leistungen"
            className="inline-flex items-center text-[#a84025] font-semibold hover:text-[#8a3520] transition-colors"
          >
            Alle Leistungen ansehen
            <ChevronRight className="ml-1 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// About Preview - RUST THEME
const AboutPreview = () => {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <p className="text-[#a84025] font-semibold tracking-[0.2em] uppercase text-sm mb-4">
              Über Uns
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mb-6 tracking-wider">
              HANDSCHLAGQUALITÄT<br />SEIT 2003
            </h2>
            <div className="w-20 h-1 bg-[#a84025] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              LINHARDSBERGER steht für professionelle Agrardienstleistungen mit 
              Handschlagqualität. Mit moderner Technik und über 20 Jahren Erfahrung 
              bieten wir zuverlässige Lösungen für Ihre Ernte.
            </p>
            <Link
              to="/ueber-uns"
              className="inline-flex items-center text-[#a84025] font-semibold hover:text-[#8a3520] transition-colors"
            >
              Mehr erfahren
              <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="grid grid-cols-2 gap-4"
          >
            <div className="bg-[#a84025] rounded-lg p-8 text-white text-center">
              <p className="font-heading text-5xl mb-2 tracking-wider">20+</p>
              <p className="text-white/80 text-sm uppercase tracking-wide">Jahre Erfahrung</p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-gray-100">
              <p className="font-heading text-5xl text-[#a84025] mb-2 tracking-wider">100%</p>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Zuverlässigkeit</p>
            </div>
            <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-gray-100">
              <p className="font-heading text-5xl text-[#a84025] mb-2 tracking-wider">10+</p>
              <p className="text-gray-600 text-sm uppercase tracking-wide">Maschinen</p>
            </div>
            <div className="bg-secondary rounded-lg p-8 text-white text-center">
              <p className="font-heading text-5xl mb-2 tracking-wider">∞</p>
              <p className="text-white/80 text-sm uppercase tracking-wide">Einsatzbereit</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section - RUST BACKGROUND
const CTASection = () => {
  return (
    <section className="py-24 bg-[#a84025]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6 tracking-wider">
            BEREIT FÜR IHRE ERNTE?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Kontaktieren Sie uns für ein unverbindliches Gespräch. 
            Wir sind für Sie da!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#a84025] font-semibold rounded-md hover:bg-gray-100 transition-all duration-300 hover:shadow-lg font-heading tracking-wider"
            >
              Jetzt anfragen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:+436646345040"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-md hover:bg-white/10 transition-all duration-300 font-heading tracking-wider"
            >
              <Phone className="mr-2 h-5 w-5" />
              Anrufen
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer - DARK WITH RUST ACCENTS
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <img
              src={ASSETS.logo}
              alt="Linhardsberger Agrarservice"
              className="h-16 brightness-0 invert mb-6"
            />
            <p className="text-gray-400 leading-relaxed">
              Professionelle Agrardienstleistungen mit Handschlagqualität seit 2003.
            </p>
          </div>

          <div>
            <h4 className="font-heading text-lg mb-6 tracking-wider text-[#a84025]">NAVIGATION</h4>
            <ul className="space-y-3">
              {["Start", "Leistungen", "Über Uns", "Galerie", "Kontakt"].map((item) => (
                <li key={item}>
                  <Link
                    to={item === "Start" ? "/" : `/${item.toLowerCase().replace(' ', '-')}`}
                    className="text-gray-400 hover:text-[#a84025] transition-colors"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg mb-6 tracking-wider text-[#a84025]">KONTAKT</h4>
            <ul className="space-y-3 text-gray-400">
              <li>Josef Leo Linhardsberger</li>
              <li>Wetzelsberg 22 / Tür 1</li>
              <li>4363 Pabneukirchen</li>
              <li className="pt-2">
                <a href="tel:+436646345040" className="hover:text-[#a84025] transition-colors">
                  0664 / 63 45 040
                </a>
              </li>
              <li>
                <a href="mailto:andrealinhardsberger@gmx.at" className="hover:text-[#a84025] transition-colors">
                  andrealinhardsberger@gmx.at
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Linhardsberger Agrarservice</p>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-[#a84025] transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#a84025] transition-colors">Datenschutz</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/436646345040?text=Hallo,%20ich%20interessiere%20mich%20für%20Ihre%20Agrardienstleistungen."
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-btn"
    className="fixed bottom-6 right-6 z-50 p-4 rounded-full bg-whatsapp text-white shadow-lg hover:scale-110 transition-transform duration-300 whatsapp-pulse"
    aria-label="WhatsApp"
  >
    <MessageCircle size={28} fill="white" />
  </a>
);

// Home Page
const HomePage = () => (
  <>
    <HeroSection />
    <HighlightBar />
    <HackguterzeugungSection />
    <ServicesPreview />
    <AboutPreview />
    <CTASection />
  </>
);

// Main App Layout
const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-grow">{children}</main>
    <Footer />
    <WhatsAppButton />
  </div>
);

// App Component
function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/leistungen" element={<LeistungenPage />} />
          <Route path="/ueber-uns" element={<UeberUnsPage />} />
          <Route path="/galerie" element={<GaleriePage />} />
          <Route path="/kontakt" element={<KontaktPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

// ==================== PAGES ====================

// Leistungen Page - RUST THEME
const LeistungenPage = () => {
  const services = [
    { title: "Hackguterzeugung", desc: "Professionelle Hackguterzeugung und Holzverarbeitung für Energieholz, Landschaftspflege und individuelle Anforderungen.", image: ASSETS.gallery[4] },
    { title: "Maishäcksler", desc: "Professionelle Maisernte mit modernster Technik für optimale Ergebnisse.", image: ASSETS.gallery[5] },
    { title: "Pick-Up für Grassilage", desc: "Effiziente Grassilage-Ernte mit leistungsstarken Pick-Up-Systemen.", image: ASSETS.gallery[0] },
    { title: "Transporte", desc: "Zuverlässige Transportlösungen für Ihre Erntegüter.", image: ASSETS.gallery[2] },
    { title: "Lader zum Verdichten (10t)", desc: "Leistungsstarker 10-Tonnen-Lader für optimale Verdichtung.", image: ASSETS.gallery[1] },
    { title: "Maisballenpresse", desc: "2 Maschinen verfügbar (variabel / Festkammer) für flexible Presslösungen.", image: ASSETS.gallery[3] },
    { title: "Quaderballenpresse", desc: "Perfekte Quaderballen für einfache Lagerung und Transport.", image: ASSETS.gallery[6] },
    { title: "Rundballenpresse", desc: "2 Maschinen (variabel / Festkammer) für vielseitige Einsatzmöglichkeiten.", image: ASSETS.gallery[7] },
    { title: "Kombipresse", desc: "Vielseitige Kombipressen-Lösungen für unterschiedliche Anforderungen.", image: ASSETS.gallery[8] },
    { title: "Mähdrescher", desc: "2 Mähdrescher, davon einer mit Hangausgleich für schwieriges Gelände.", image: ASSETS.heroNew },
    { title: "Strohverarbeitung", desc: "Professionelle Strohverarbeitung mit Längen bis 4 cm.", image: ASSETS.gallery[0] }
  ];

  return (
    <div className="pt-20">
      {/* Hero - RUST */}
      <section className="bg-[#a84025] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-widest">
              UNSERE LEISTUNGEN
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Professionelle Agrardienstleistungen mit modernster Technik und höchster Zuverlässigkeit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                variants={fadeIn}
                data-testid={`service-card-${idx}`}
                className="service-card group"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl text-gray-900 mb-3 group-hover:text-[#a84025] transition-colors tracking-wider">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

// Über Uns Page - RUST THEME
const UeberUnsPage = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-[#a84025] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-widest">
              ÜBER UNS
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Familienbetrieb mit Leidenschaft für Landwirtschaft seit 2003.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <img
                src={ASSETS.gallery[8]}
                alt="Linhardsberger Maschinen"
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-6 tracking-wider">
                HANDSCHLAGQUALITÄT AUS ÖSTERREICH
              </h2>
              <div className="w-20 h-1 bg-[#a84025] rounded-full mb-8" />
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  LINHARDSBERGER steht seit 2003 für professionelle Agrardienstleistungen 
                  mit echter Handschlagqualität. Als Familienbetrieb aus Pabneukirchen 
                  verbinden wir traditionelle Werte mit modernster Technik.
                </p>
                <p>
                  Mit unserer umfangreichen Maschinenausstattung – von Maishäckslern über 
                  Ballenpressen bis hin zu Mähdreschern mit Hangausgleich – bieten wir 
                  Ihnen alles aus einer Hand.
                </p>
                <p>
                  Unser Ziel ist es, unseren Kunden einen starken, flexiblen und 
                  effizienten Service zu bieten. Verlässlichkeit, Pünktlichkeit und 
                  Qualität sind dabei unsere obersten Prioritäten.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Values */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24"
          >
            {[
              { title: "Erfahrung", desc: "Über 20 Jahre Expertise in der Landwirtschaft." },
              { title: "Zuverlässigkeit", desc: "Pünktlich und verlässlich – darauf können Sie zählen." },
              { title: "Moderne Technik", desc: "Aktuelle Maschinen für beste Ergebnisse." }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeIn}
                className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100 hover:border-[#a84025] transition-colors"
              >
                <h3 className="font-heading text-xl text-gray-900 mb-4 tracking-wider">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

// Galerie Page
const GaleriePage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-[#a84025] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-widest">
              GALERIE
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Einblicke in unsere Arbeit und unseren Maschinenpark.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="masonry-grid"
          >
            {[ASSETS.heroNew, ...ASSETS.gallery].map((img, idx) => (
              <motion.div
                key={idx}
                variants={fadeIn}
                data-testid={`gallery-item-${idx}`}
                className="masonry-item group cursor-pointer overflow-hidden rounded-lg"
                onClick={() => setSelectedImage(img)}
              >
                <img
                  src={img}
                  alt={`Galerie ${idx + 1}`}
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-[#a84025] transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage}
              alt="Vergrößert"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Kontakt Page - RUST THEME
const KontaktPage = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-[#a84025] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-widest">
              KONTAKT
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Wir sind für Sie da – kontaktieren Sie uns!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="font-heading text-2xl text-gray-900 mb-6 tracking-wider">NACHRICHT SENDEN</h2>
              <form
                action="https://formsubmit.co/andrealinhardsberger@gmx.at"
                method="POST"
                data-testid="contact-form"
                className="space-y-6"
              >
                <input type="hidden" name="_subject" value="Neue Anfrage von Website" />
                <input type="hidden" name="_captcha" value="false" />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      data-testid="contact-name"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md focus:border-[#a84025] focus:ring-2 focus:ring-[#a84025]/20 outline-none transition-all"
                      placeholder="Ihr Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      data-testid="contact-phone"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md focus:border-[#a84025] focus:ring-2 focus:ring-[#a84025]/20 outline-none transition-all"
                      placeholder="Ihre Telefonnummer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    data-testid="contact-email"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md focus:border-[#a84025] focus:ring-2 focus:ring-[#a84025]/20 outline-none transition-all"
                    placeholder="ihre@email.at"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    data-testid="contact-message"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-md focus:border-[#a84025] focus:ring-2 focus:ring-[#a84025]/20 outline-none transition-all resize-none"
                    placeholder="Wie können wir Ihnen helfen?"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="btn-primary"
                >
                  Nachricht senden
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="space-y-8"
            >
              <div className="bg-gray-50 rounded-lg p-8 border border-gray-100">
                <h2 className="font-heading text-2xl text-gray-900 mb-6 tracking-wider">KONTAKTDATEN</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <MapPin className="text-[#a84025] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="font-semibold text-gray-900">Josef Leo Linhardsberger</p>
                      <p className="text-gray-600">Wetzelsberg 22 / Tür 1</p>
                      <p className="text-gray-600">4363 Pabneukirchen</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Phone className="text-[#a84025] flex-shrink-0" size={20} />
                    <a href="tel:+436646345040" className="text-gray-900 hover:text-[#a84025] transition-colors font-semibold">
                      0664 / 63 45 040
                    </a>
                  </div>
                  <div className="flex items-center gap-4">
                    <Mail className="text-[#a84025] flex-shrink-0" size={20} />
                    <a href="mailto:andrealinhardsberger@gmx.at" className="text-gray-900 hover:text-[#a84025] transition-colors">
                      andrealinhardsberger@gmx.at
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="w-full h-[300px] rounded-lg overflow-hidden border border-gray-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10598.254!2d14.7851!3d48.3225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47732847c9e3c7c1%3A0x400f7d1c69679d0!2s4363%20Pabneukirchen%2C%20Austria!5e0!3m2!1sen!2sat!4v1703847600000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Standort"
                  data-testid="google-map"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Impressum Page
const ImpressumPage = () => {
  return (
    <div className="pt-20">
      <section className="bg-[#a84025] py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h1 className="font-heading text-3xl md:text-4xl text-white tracking-widest">IMPRESSUM</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="prose prose-lg max-w-none">
            <h2 className="font-heading text-xl text-gray-900 mb-4 tracking-wider">Angaben gemäß § 5 ECG</h2>
            <address className="not-italic text-gray-600 mb-8">
              <strong className="text-gray-900">Josef Leo Linhardsberger</strong><br />
              Wetzelsberg 22 / Tür 1<br />
              4363 Pabneukirchen<br />
              Österreich
            </address>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">Kontakt</h2>
            <p className="text-gray-600 mb-8">
              Telefon: <a href="tel:+436646345040" className="text-[#a84025] hover:underline">0664 / 63 45 040</a><br />
              E-Mail: <a href="mailto:andrealinhardsberger@gmx.at" className="text-[#a84025] hover:underline">andrealinhardsberger@gmx.at</a>
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">Unternehmensgegenstand</h2>
            <p className="text-gray-600 mb-8">Agrarservice, Hackguterzeugung, Agrardienstleistungen</p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">UID-Nummer</h2>
            <p className="text-gray-600 mb-8">ATU56687945</p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">Gewerbeberechtigung</h2>
            <p className="text-gray-600 mb-8">Beginn der Tätigkeit: 14.01.2003</p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">Aufsichtsbehörde</h2>
            <p className="text-gray-600 mb-8">Bezirkshauptmannschaft Perg</p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">Haftungsausschluss</h2>
            <p className="text-gray-600 mb-4">
              <strong className="text-gray-900">Haftung für Inhalte:</strong> Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.
            </p>
            <p className="text-gray-600 mb-4">
              <strong className="text-gray-900">Haftung für Links:</strong> Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben.
            </p>
            <p className="text-gray-600">
              <strong className="text-gray-900">Urheberrecht:</strong> Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem österreichischen Urheberrecht.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  return (
    <div className="pt-20">
      <section className="bg-[#a84025] py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h1 className="font-heading text-3xl md:text-4xl text-white tracking-widest">DATENSCHUTZ</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="prose prose-lg max-w-none text-gray-600">
            <h2 className="font-heading text-xl text-gray-900 mb-4 tracking-wider">1. Datenschutz auf einen Blick</h2>
            <p className="mb-4">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen.
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">2. Verantwortlicher</h2>
            <p className="mb-4">
              Josef Leo Linhardsberger<br />
              Wetzelsberg 22 / Tür 1<br />
              4363 Pabneukirchen, Österreich<br />
              Telefon: 0664 / 63 45 040<br />
              E-Mail: andrealinhardsberger@gmx.at
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">3. Datenerfassung auf dieser Website</h2>
            <h3 className="font-heading text-lg text-gray-800 mb-3 tracking-wide">Kontaktformular</h3>
            <p className="mb-4">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
            </p>

            <h3 className="font-heading text-lg text-gray-800 mb-3 tracking-wide">Server-Log-Dateien</h3>
            <p className="mb-4">
              Der Provider erhebt automatisch Informationen in Server-Log-Dateien: Browsertyp, Betriebssystem, Referrer URL, IP-Adresse, Uhrzeit der Anfrage.
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">4. Cookies</h2>
            <p className="mb-4">
              Diese Website verwendet technisch notwendige Cookies. Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden.
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">5. Ihre Rechte</h2>
            <p className="mb-4">
              Sie haben jederzeit das Recht auf Auskunft, Berichtigung oder Löschung Ihrer gespeicherten personenbezogenen Daten.
            </p>
            <p className="mb-4">
              <strong className="text-gray-900">Zuständige Aufsichtsbehörde:</strong><br />
              Österreichische Datenschutzbehörde<br />
              Barichgasse 40-42, 1030 Wien<br />
              E-Mail: dsb@dsb.gv.at
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">6. Google Maps</h2>
            <p className="mb-4">
              Diese Seite nutzt Google Maps. Anbieter ist Google Ireland Limited. Zur Nutzung wird Ihre IP-Adresse gespeichert und an Server von Google übertragen.
            </p>

            <h2 className="font-heading text-xl text-gray-900 mb-4 mt-8 tracking-wider">7. Aktualität</h2>
            <p>
              Diese Datenschutzerklärung ist aktuell gültig (Stand: Dezember 2025).
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default App;
